import html2pdf from "html2pdf.js";
import JsBarcode from "jsbarcode";

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("fr-FR");
};

const FACTURE_PDF_CSS = `
  @page { size: A4; margin: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; }
  .page { width: 100%; box-sizing: border-box; padding: 10mm; min-height: 296mm; display: flex; flex-direction: column; }
  .footer-wrap { margin-top: auto; }

  /* Ensure background colors are preserved in browser print */
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @media print {
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    table.items thead th { background: #d9d9d9 !important; }
    table.tva-mini thead th { background: #d9d9d9 !important; }
  }

  .header { display: flex; justify-content: space-between; align-items: flex-start; }
  .header-left { width: 26%; }
  .header-left img { height: 92px; }
  .header-middle { width: 30%; display: flex; justify-content: center; }
  .header-right { width: 34%; text-align: right; }

  .reg-box { border: 2px solid #2f6fff; border-radius: 4px; padding: 8px 10px; width: 220px; font-size: 12px; color: #2f6fff; }
  .reg-row { display: flex; justify-content: space-between; gap: 10px; padding: 6px 0; border-bottom: 1px dotted #2f6fff; }
  .reg-row:last-child { border-bottom: none; }
  .reg-row span:last-child { color: #c03a3a; font-weight: 700; }

  .facture-title { font-weight: 800; font-size: 26px; letter-spacing: 1px; }
  .facture-number { margin-top: 6px; font-size: 13px; }
  .barcode { margin-top: 10px; margin-left: auto; width: 190px; height: 44px; display: flex; justify-content: flex-end; }
  .barcode img { width: 190px; height: 44px; object-fit: contain; }
  .barcode-text { margin-top: 4px; font-size: 10px; letter-spacing: 2px; }

  .section-line { margin-top: 10px; height: 3px; background: #111; opacity: 0.75; }

  .client-row { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 8px; }
  .client { width: 55%; }
  .client-title { font-weight: 700; font-size: 12px; margin-bottom: 6px; }
  .client-title::after { content: ""; display: block; height: 2px; background: #111; opacity: 0.75; margin-top: 6px; }
  .kv { font-size: 12px; line-height: 1.55; }
  .date-right { width: 40%; text-align: center; font-size: 12px; padding-top: 24px; }

  table { width: 100%; border-collapse: collapse; font-size: 12px; }

  table.items thead th { background: #d9d9d9; padding: 8px; border: 1px solid #cfcfcf; text-align: left; }
  table.items tbody td { padding: 8px; border: 1px solid #e1e1e1; }

  .totals-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }

  table.tva-mini { width: 42%; margin-left: 20%; }
  table.tva-mini thead th { background: #d9d9d9; padding: 8px; border: 1px solid #cfcfcf; text-align: left; }
  table.tva-mini tbody td { padding: 8px; border: 1px solid #e1e1e1; }

  .totals-right { width: 38%; margin-left: auto; }
  table.totals { width: 100%; }
  table.totals td { padding: 8px 10px; border-bottom: 1px solid #eee; }
  table.totals td.label { width: 55%; }
  table.totals td.value { text-align: right; width: 45%; }

  .net-row { margin-top: 12px; display: flex; justify-content: space-between; background: #d9d9d9; padding: 12px 14px; font-weight: 800; border: 1px solid #cfcfcf; }
  .net-label { letter-spacing: 0.5px; }
  .net-value { text-align: right; }

  .amount-words { font-size: 12px; margin-top: 4px; line-height: 1.6; width: 60%; }
.amount-words-value { text-transform: lowercase; }
  .sign-table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 11px; }
  .sign-table th { border: 1px solid #111; padding: 8px; text-align: center; font-weight: 700; }
  .sign-table td { border: 1px solid #111; height: 46px; padding: 8px; vertical-align: middle; }

  .footer-line { margin-top: 20px; height: 3px; background: #111; opacity: 0.75; }
  .footer { margin-top: 10px; display: flex; justify-content: space-between; font-size: 10.5px; }
  .footer-col { width: 32%; }
  .footer-title { font-weight: 700; margin-bottom: 8px; }
  .page-number { margin-top: 12px; text-align: center; font-size: 11px; }
`;

const formatMoneyDt = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "";
  }
  return `${Number(value).toFixed(3)} DT`;
};

const buildBarcodeDataUri = (value) => {
  try {
    const text = value ? String(value) : "";
    if (!text) return "";

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    JsBarcode(svg, text, {
      format: "CODE128",
      displayValue: false,
      margin: 0,
      height: 44,
      width: 1.2,
    });
    const svgStr = new XMLSerializer().serializeToString(svg);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgStr)}`;
  } catch (e) {
    return "";
  }
};

const buildFacturePageHtml = (
  facture,
  { pageNumber = 1, pageCount = 1, docTitle = "FACTURE" } = {}
) => {
  const publicUrl = (typeof process !== "undefined" && process?.env?.PUBLIC_URL) ? process.env.PUBLIC_URL : "";
  const logoPath = `${publicUrl || ""}/taki.jpg`;
  const logoUrl = new URL(logoPath, window.location.origin).toString();
  const barcodeSrc = buildBarcodeDataUri(facture.number);
  const isAvoir = String(docTitle || "").toUpperCase() === "AVOIR";
  const dateLabel = isAvoir ? "Date avoir" : "Date facture";
  const amountWordsLabel = isAvoir
    ? "Arrêtée la présente avoir à la somme de :"
    : "Arrêtée la présente facture à la somme de :";

  const formatPaymentMethod = (value) => {
    const v = value ? String(value) : "";
    if (!v) return "";
    const map = {
      payment: "Versement Bancaire",
      bank_transfer: "Versement Bancaire",
      credit_card: "Carte Bancaire (Paiement en ligne)",
      d17: "D17",
      check: "Chèque",
      billOfExchange: "Lettre de change",
      cash: "Espèce",
      poste: "Poste",
    };
    return map[v] || v;
  };

  const paymentMethod = formatPaymentMethod(facture?.paymentMethod);

  const rowsHtml = facture.items
    .map((item, index) => {
      const startValue = index === 0 ? formatDate(facture.offerStartDate) : "";
      const endValue = index === 0 ? formatDate(facture.offerEndDate) : "";

      return `
        <tr>
          <td style="padding:8px;border:1px solid #bdbdbd;">${index + 1}</td>
          <td style="padding:8px;border:1px solid #bdbdbd;">${item.description}</td>
          <td style="padding:8px;border:1px solid #bdbdbd;text-align:center;">${startValue}</td>
          <td style="padding:8px;border:1px solid #bdbdbd;text-align:center;">${endValue}</td>
          <td style="padding:8px;border:1px solid #bdbdbd;text-align:center;">${item.quantity}</td>
          <td style="padding:8px;border:1px solid #bdbdbd;text-align:center;">${item.unit}</td>
          <td style="padding:8px;border:1px solid #bdbdbd;text-align:right;">${formatMoneyDt(facture.baseTva)
        }</td>
          <td style="padding:8px;border:1px solid #bdbdbd;text-align:center;">${item.tva}%</td>
          <td style="padding:8px;border:1px solid #bdbdbd;text-align:right;">${formatMoneyDt(
          item.totalTtc - facture.stamp
        )}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <div class="page">
      <div class="header">
        <div class="header-left">
          <img src="${logoUrl}" alt="Taki" />
        </div>

       

        <div class="header-right">
          <div class="facture-title">${docTitle}</div>
          <div class="facture-number">N° <b>${facture.number}</b></div>
          <div class="barcode">${barcodeSrc ? `<img src="${barcodeSrc}" alt="barcode" />` : ""}</div>
          <div class="barcode-text">${facture.number}</div>
        </div>
      </div>

      <div class="section-line"></div>

      <div class="client-row">
        <div class="client">
          <div class="client-title">CLIENT</div>
          <div class="kv">
            <div>${facture.client.name}</div>
            ${facture?.client?.mf ? `<div> M/F ${facture.client.mf}</div>` : ""}
          </div>
        </div>
        <div class="date-right">
          <div>${dateLabel} ${formatDate(facture.date)}</div>
          ${isAvoir && facture?.invoiceDate ? `<div>Date facture ${formatDate(facture.invoiceDate)}</div>` : ""}
        </div>
      </div>

      <div style="height: 18px;"></div>

      <table class="items">
        <thead>
          <tr>
            <th style="width: 40px;">#</th>
            <th >Description</th>
            <th style="width: 90px; text-align:center;">Date début</th>
            <th style="width: 90px; text-align:center;">Date fin</th>
            <th style="width: 90px; text-align:center;">Quantité</th>
            <th style="width: 80px; text-align:center;">Unité</th>
            <th style="width: 110px; text-align:right;">PU HT</th>
            <th style="width: 70px; text-align:center;">TVA</th>
            <th style="width: 120px; text-align:right;">Total TTC</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>

      <div style="height: 18px;"></div>

      <div class="totals-row">
        <table class="tva-mini">
          <thead>
            <tr>
              <th style="width: 70px;">TVA</th>
              <th>Base</th>
              <th style="width: 110px;">Montant</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="text-align:center;">19 %</td>
              <td style="text-align:right;">${formatMoneyDt(facture.baseTva)}</td>
              <td style="text-align:right;">${formatMoneyDt(facture.totalTva)}</td>
            </tr>
          </tbody>
        </table>

        <div class="totals-right">
          <table class="totals">
            <tbody>
              <tr>
                <td class="label">TOTAL HT</td>
                <td class="value">${formatMoneyDt(facture.totalHt)}</td>
              </tr>
              <tr>
                <td class="label">BASE TVA</td>
                <td class="value">${formatMoneyDt(facture.baseTva)}</td>
              </tr>
              <tr>
                <td class="label">TOTAL TVA</td>
                <td class="value">${formatMoneyDt(facture.totalTva)}</td>
              </tr>
              ${isAvoir ? "" : `
              <tr>
                <td class="label">Timbre fiscal</td>
                <td class="value">${formatMoneyDt(facture.stamp)}</td>
              </tr>
              `}
            </tbody>
          </table>

          <div class="net-row">
            <div class="net-label">NET À PAYER</div>
            <div class="net-value">${formatMoneyDt(facture.netToPay)}</div>
          </div>
        </div>
      </div>

      <div style="height: 16px;"></div>

      <div class="amount-words">
        ${amountWordsLabel}<br />
        <span class="amount-words-value">${facture.amountInWords}</span>
      </div>

      <table class="sign-table">
        <thead>
          <tr>
            <th style="width: 70%;">MODE REGLEMENT</th>
            <th style="width: 30%;">SIGNATURE</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>${paymentMethod}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <div class="footer-wrap">
        <div class="footer-line"></div>

        <div class="footer">
          <div class="footer-col">
            <div class="footer-title">TAKIACADEMY</div>
            <div>Avenue Mongi Grira Sahloul 3</div>
            <div>4054 Sousse</div>
            <div>Code TVA : 1432445L/A/M/000</div>
          </div>
          <div class="footer-col">
            <div class="footer-title">INFORMATIONS DE CONTACT</div>
            <div>M +21673832000</div>
            <div>contact@takiacademy.com</div>
            <div>www.takiacademy.com</div>
          </div>
          <div class="footer-col">
            <div class="footer-title">INFORMATIONS BANCAIRES</div>
            <div>Banque BIAT-Banque Internationale Arabe de Tunisie</div>
            <div>IBAN TN5908139031011000079088</div>
            <div>Banque Zitouna-Banque Zitouna</div>
            <div>IBAN TN5925060000000031704186</div>
            <div>Banque Poste tunisienne</div>
            <div>IBAN TN59175030000000311792828</div>
          </div>
        </div>

        <div class="page-number">Page ${pageNumber} / ${pageCount}</div>
      </div>
    </div>
  `;
};

const buildBulkFactureHtml = (factures, { docTitle = "FACTURE" } = {}) => {
  const pageCount = factures.length;
  return factures
    .map((f, idx) => {
      const pageHtml = buildFacturePageHtml(f, {
        pageNumber: idx + 1,
        pageCount,
        docTitle,
      });
      const breakAfter = idx + 1 < pageCount ? "page-break-after: always;" : "";
      return `<div style="${breakAfter}">${pageHtml}</div>`;
    })
    .join("");
};

const waitForImages = (rootEl) => {
  const images = Array.from(rootEl.querySelectorAll("img"));
  if (images.length === 0) return Promise.resolve();

  return Promise.all(
    images.map((img) => {
      try {
        img.crossOrigin = "anonymous";
      } catch (e) {
        // ignore
      }
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });
    })
  ).then(() => undefined);
};

const waitForLayout = (el) => {
  if (!el) return Promise.resolve();
  // Force a reflow to ensure styles are applied before capture
  el.getBoundingClientRect();
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
};

const exportDocsPdf = (docs, { docTitle = "FACTURE", filenamePrefix = "facture" } = {}) => {
  if (!Array.isArray(docs) || docs.length === 0) {
    return;
  }

  const pagesHtml = buildBulkFactureHtml(docs, { docTitle });

  const styleEl = document.createElement("style");
  styleEl.setAttribute("data-facture-pdf", "true");
  styleEl.textContent = FACTURE_PDF_CSS;
  document.head.appendChild(styleEl);

  const overlay = document.createElement("div");
  overlay.setAttribute("data-facture-pdf", "true");
  overlay.style.position = "fixed";
  overlay.style.left = "0";
  overlay.style.top = "0";
  overlay.style.right = "0";
  overlay.style.bottom = "0";
  overlay.style.background = "white";
  overlay.style.zIndex = "2147483647";
  overlay.style.overflow = "auto";

  const container = document.createElement("div");
  container.setAttribute("data-facture-pdf", "true");
  container.style.width = "794px";
  container.style.margin = "0 auto";
  container.innerHTML = pagesHtml;
  overlay.appendChild(container);

  document.body.appendChild(overlay);

  const filename =
    docs.length === 1
      ? `${filenamePrefix}-${docs[0]?.number || docs[0]?.id || "export"}.pdf`
      : `${filenamePrefix}s.pdf`;

  const opt = {
    margin: 0,
    filename,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: 0,
      windowWidth: 794,
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    pagebreak: { mode: ["css", "legacy"] },
  };

  const cleanup = () => {
    if (overlay.parentNode) {
      document.body.removeChild(overlay);
    }
    if (styleEl.parentNode) {
      document.head.removeChild(styleEl);
    }
  };

  waitForLayout(container)
    .then(() => waitForImages(container))
    .then(() => html2pdf().set(opt).from(container).save())
    .finally(() => cleanup());
};

const printDocs = (docs, { docTitle = "FACTURE" } = {}) => {
  if (!Array.isArray(docs) || docs.length === 0) {
    return;
  }

  const pagesHtml = buildBulkFactureHtml(docs, { docTitle });

  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    return;
  }

  const doc = printWindow.document;

  const writeDocument = () => {
    try {
      doc.open();
      doc.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${docTitle}</title>
    <style>${FACTURE_PDF_CSS}</style>
    <style>
      .print-actions { position: sticky; top: 0; background: #fff; border-bottom: 1px solid #eee; padding: 10px; z-index: 9999; display: flex; gap: 8px; justify-content: flex-end; }
      .print-actions button { font: inherit; padding: 8px 12px; border-radius: 6px; border: 1px solid #ccc; background: #f7f7f7; cursor: pointer; }
      @media print { .print-actions { display: none !important; } }
    </style>
  </head>
  <body></body>
</html>`);
      doc.close();

      if (doc.body) {
        doc.body.innerHTML = `
          <div class="print-actions">
            <button id="btn-print" type="button">Imprimer</button>
            <button id="btn-close" type="button">Fermer</button>
          </div>
          ${pagesHtml}
        `;

        const btnPrint = doc.getElementById("btn-print");
        const btnClose = doc.getElementById("btn-close");
        if (btnPrint) {
          btnPrint.addEventListener("click", () => {
            try {
              printWindow.focus();
              printWindow.print();
            } catch (e) {
              // ignore
            }
          });
        }
        if (btnClose) {
          btnClose.addEventListener("click", () => {
            try {
              printWindow.close();
            } catch (e) {
              // ignore
            }
          });
        }
      }
    } catch (e) {
      // ignore
    }
  };

  writeDocument();

  let hasPrinted = false;
  const doPrint = () => {
    if (hasPrinted) return;
    hasPrinted = true;
    try {
      printWindow.focus();
      printWindow.print();
    } finally {
      setTimeout(() => {
        try {
          printWindow.close();
        } catch (e) {
          // ignore
        }
      }, 300);
    }
  };

  const root = doc.body;
  const waitWithTimeout = (promise, timeoutMs) => {
    let timeoutId;
    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(resolve, timeoutMs);
    });
    return Promise.race([promise.finally(() => clearTimeout(timeoutId)), timeoutPromise]);
  };

  waitForLayout(root)
    .then(() => waitWithTimeout(waitForImages(root), 1500))
    .finally(() => {
      setTimeout(doPrint, 0);
    });
};

export const exportFacturesPdf = (factures) => {
  if (!Array.isArray(factures) || factures.length === 0) {
    return;
  }

  exportDocsPdf(factures, { docTitle: "FACTURE", filenamePrefix: "facture" });
};

export const printFactures = (factures) => {
  if (!Array.isArray(factures) || factures.length === 0) {
    return;
  }

  printDocs(factures, { docTitle: "FACTURE" });
};

export const exportAvoirsPdf = (avoirs) => {
  exportDocsPdf(avoirs, { docTitle: "AVOIR", filenamePrefix: "avoir" });
};

export const printAvoirs = (avoirs) => {
  printDocs(avoirs, { docTitle: "AVOIR" });
};
